import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, ShoppingBag, Check } from 'lucide-react';
import { openLink } from '@tma.js/sdk-react';

import {
  useGetIapUserQuery,
  useGetPurchasesQuery,
  useUpdateIapUserMutation,
} from '@/services/customizationApi';
import { cdnImage } from '@/lib/cdnImage';
import { cn } from '@/lib/cn';
import { env } from '@/env';
import { useHaptics } from '@/hooks/useHaptics';

interface CustomizeSheetProps {
  /** QLASH user id — used to load IAP user record + their purchases. */
  userId: number | string | undefined | null;
  open: boolean;
  onClose: () => void;
}

/**
 * Full-screen overlay (Telegram WebView doesn't play well with native iOS
 * bottom-sheet semantics, so we go full overlay like `WatchPartyHub`).
 *
 * Lists the user's owned banners as a 2-column grid; tapping one calls
 * `PUT /users/{iapUserId}` body `{banner: id}` and instantly reflects the
 * new selection via the IapUser cache invalidation. A "Get more banners"
 * CTA at the bottom opens `env.shopUrl` in the system browser via Telegram's
 * `openLink` — banner purchases live on the mobile app's native IAP screen.
 */
export function CustomizeSheet({ userId, open, onClose }: CustomizeSheetProps) {
  const { t } = useTranslation('general');
  const haptics = useHaptics();
  const { data: iapUser, isLoading: iapLoading } = useGetIapUserQuery(userId ?? '', {
    skip: userId == null || !open,
  });
  const iapUserId = iapUser?._id;
  const { data: purchases, isLoading: purchasesLoading } = useGetPurchasesQuery(
    { user: iapUserId ?? '' },
    { skip: !iapUserId || !open },
  );
  const [updateIapUser, mutation] = useUpdateIapUserMutation();

  // Some purchases may resolve to the same banner id (re-purchase / migration
  // artefacts). Dedup on banner._id and ignore entries without a banner.
  const banners = useMemo(() => {
    const seen = new Set<string>();
    const out: { id: string; name: string; mediaUri: string; type: string }[] = [];
    (purchases ?? []).forEach((p) => {
      const b = p.banner;
      if (!b?._id || seen.has(b._id)) return;
      seen.add(b._id);
      out.push({
        id: b._id,
        name: b.name ?? '—',
        mediaUri: b.media_uri,
        type: b.type ?? 'common',
      });
    });
    return out;
  }, [purchases]);

  const activeBannerId = iapUser?.banner?._id ?? null;
  const [pendingId, setPendingId] = useState<string | null>(null);

  if (!open) return null;

  const handlePick = (id: string): void => {
    if (!iapUserId || mutation.isLoading) return;
    haptics.selection();
    setPendingId(id);
    updateIapUser({ id: iapUserId, body: { banner: id } })
      .unwrap()
      .catch((err) => {
        console.warn('[customize] update failed:', err);
      })
      .finally(() => setPendingId(null));
  };

  const handleGetMore = (): void => {
    haptics.impact('light');
    openLink.ifAvailable(env.shopUrl);
  };

  const loading = iapLoading || purchasesLoading;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-bg/95 backdrop-blur">
      <header className="flex items-start justify-between border-b border-border px-4 py-3">
        <div>
          <h2 className="text-base font-bold text-brand">
            {t('banner.customize_title', { defaultValue: 'Customize banner' })}
          </h2>
          <p className="mt-0.5 text-xs text-fg-muted">
            {t('banner.customize_subtitle', {
              defaultValue: 'Pick one of your unlocked banners.',
            })}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('close', { defaultValue: 'Close' })}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-bg-secondary text-fg transition-opacity active:opacity-80"
        >
          <X aria-hidden className="h-5 w-5" />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto p-4">
        {loading && (
          <p className="py-10 text-center text-sm text-fg-muted">
            {t('banner.loading', { defaultValue: 'Loading your banners…' })}
          </p>
        )}

        {!loading && banners.length === 0 && (
          <div className="rounded-xl bg-bg-secondary p-6 text-center text-sm text-fg-muted">
            {t('banner.empty', {
              defaultValue:
                "You don't own any banners yet. Unlock some in the QLASH app shop.",
            })}
          </div>
        )}

        {!loading && banners.length > 0 && (
          <ul className="grid grid-cols-2 gap-3">
            {banners.map((b) => {
              const isActive = b.id === activeBannerId;
              const isPending = b.id === pendingId;
              return (
                <li key={b.id}>
                  <button
                    type="button"
                    onClick={() => handlePick(b.id)}
                    disabled={mutation.isLoading}
                    className={cn(
                      'group relative flex w-full flex-col gap-2 overflow-hidden rounded-xl p-1 text-left transition-opacity',
                      isActive ? 'ring-2 ring-brand ring-offset-2 ring-offset-bg' : '',
                      mutation.isLoading && !isPending ? 'opacity-50' : 'active:opacity-80',
                    )}
                  >
                    <div className="relative aspect-[16/9] overflow-hidden rounded-lg bg-bg-tertiary">
                      {b.mediaUri && (
                        <img
                          src={cdnImage(b.mediaUri)}
                          alt={b.name}
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                      )}
                      {isActive && (
                        <span className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-brand text-brand-fg">
                          <Check aria-hidden className="h-4 w-4" />
                        </span>
                      )}
                      {isPending && (
                        <span className="absolute inset-0 flex items-center justify-center bg-bg/60 text-xs font-semibold text-fg">
                          {t('banner.applying', { defaultValue: 'Applying…' })}
                        </span>
                      )}
                    </div>
                    <p className="px-1 text-xs font-semibold text-fg">{b.name}</p>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <footer className="border-t border-border px-4 py-3">
        <button
          type="button"
          onClick={handleGetMore}
          className="flex w-full items-center justify-center gap-2 rounded-full bg-brand py-3 text-sm font-bold uppercase text-brand-fg transition-opacity active:opacity-80"
        >
          <ShoppingBag aria-hidden className="h-4 w-4" />
          {t('banner.get_more', { defaultValue: 'Get more banners' })}
        </button>
      </footer>
    </div>
  );
}
